import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import { menuItemService } from '../../../../services/menuItemService';
import { menuCategoryService } from '../../../../services/menuCategoryService';
import { apiService } from '../../../../services/api';
import { CreateMenuItemDTO, MenuCategoryDTO, CreateMenuItemRecipeDTO } from '../../../../types';
import {
  ArrowLeft, ChefHat, FlaskConical, Trash2, Plus,
  Receipt, Clock3, Settings2, CalendarCheck, Image, Upload,
  Hash, Zap, Percent, BadgeDollarSign, AlertCircle,
} from 'lucide-react';
import '../RestaurantPage.css';

import { getServerErrorMessage } from '../../../../utils/errorUtils';
import { compressImage } from '../../../../utils/imageUtils';

/* ── Image upload constraints ────────────────────────────────────────────── */
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMAGE_SOURCE_BYTES = 8 * 1024 * 1024; // 8 MB — original file, before compression
/* ── Local types ──────────────────────────────────────────────────────────── */
interface RecipeRow {
  key: number;
  productId: number | '';
  unitId: number | '';
  quantity: number;
  unitCost: number;
  notes: string;
}
interface ProductOption { id: number; name: string; code?: string; }
interface UnitOption    { unitId: number; name: string; shortName?: string; }
interface TaxOption     { taxId: number; taxName: string; taxType: string; taxPercentage: number; }

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/* ── Helpers ──────────────────────────────────────────────────────────────── */
function unwrapList<T>(res: any): T[] {
  const d = (res.data as any)?.data;
  return Array.isArray(d) ? d : (d?.data ?? []);
}

/* ══════════════════════════════════════════════════════════════════════════ */
const MenuItemCreatePage: React.FC = () => {
  const navigate   = useNavigate();
  const { id }     = useParams<{ id: string }>();
  const { t }      = useTranslation();
  const isEdit     = Boolean(id);

  /* ── state ── */
  const [saving,      setSaving]      = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /* ── reference data ── */
  const [categories, setCategories] = useState<MenuCategoryDTO[]>([]);
  const [products,   setProducts]   = useState<ProductOption[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);
  const [salesTaxes, setSalesTaxes] = useState<TaxOption[]>([]);

  /* ── form fields ── */
  const [name,            setName]            = useState('');
  const [menuCode,        setMenuCode]        = useState('');
  const [description,     setDescription]     = useState('');
  const [menuCategoryId,  setMenuCategoryId]  = useState<number | ''>('');
  const [price,           setPrice]           = useState<number | ''>('');
  const [imageUrl,        setImageUrl]        = useState('');
  const [isAvailable,     setIsAvailable]     = useState(true);
  const [isActive,        setIsActive]        = useState(true);
  const [prepTimeMinutes, setPrepTimeMinutes] = useState<number | ''>('');
  const [sortOrder,       setSortOrder]       = useState(0);

  /* ── image upload ── */
  type ImageStatus = 'idle' | 'uploading' | 'success' | 'error';
  const [imageStatus, setImageStatus] = useState<ImageStatus>('idle');
  const [imageError,  setImageError]  = useState<string | null>(null);
  const [localImagePreview, setLocalImagePreview] = useState<string | null>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);

  /* ── cost ── */
  const [manualCost, setManualCost] = useState<number | ''>('');

  /* ── operational ── */
  const [kotBotType,           setKotBotType]           = useState('None');
  const [serviceType,          setServiceType]          = useState('All');
  const [selectedTaxId,        setSelectedTaxId]        = useState<number | ''>('');
  const [taxRate,              setTaxRate]              = useState<number | ''>(0);
  const [taxType,              setTaxType]              = useState('');
  const [availableFrom,        setAvailableFrom]        = useState('');
  const [availableTo,          setAvailableTo]          = useState('');
  const [availableDays,        setAvailableDays]        = useState(0);

  /* ── recipe ── */
  const [recipes, setRecipes] = useState<RecipeRow[]>([]);
  const [rowKey,  setRowKey]  = useState(0);

  /* ── load reference data ── */
  useEffect(() => {
    menuCategoryService.getAll().then(res => setCategories(unwrapList(res))).catch(() => {});

    apiService.get('/products', { pageNumber: 1, pageSize: 500 }).then(res => {
      const arr = unwrapList<any>(res);
      setProducts(arr.map(p => ({ id: p.id, name: p.name, code: p.sku ?? p.code ?? p.productCode })));
    }).catch(() => {});

    apiService.get('/units', { pageNumber: 1, pageSize: 200 }).then(res => {
      const arr = unwrapList<any>(res);
      setUnits(arr.map((u: any) => ({
        unitId: u.unitId ?? u.id,
        name: u.unitName ?? u.name,
        shortName: u.unitShortName ?? u.shortName ?? u.abbreviation,
      })));
    }).catch(() => {});

    apiService.get('/taxes/sales').then(res => {
      const d = (res.data as any)?.data;
      setSalesTaxes(Array.isArray(d) ? d : []);
    }).catch(() => {});

    if (isEdit && id) {
      setLoadingData(true);
      menuItemService.getById(parseInt(id)).then(res => {
        const d = (res.data as any)?.data ?? res.data;
        setName(d.name ?? '');
        setMenuCode(d.menuCode ?? '');
        setDescription(d.description ?? '');
        setMenuCategoryId(d.menuCategoryId ?? '');
        setPrice(d.price ?? '');
        setImageUrl(d.imageUrl ?? '');
        setIsAvailable(d.isAvailable ?? true);
        setIsActive(d.isActive ?? true);
        setPrepTimeMinutes(d.prepTimeMinutes ?? '');
        setSortOrder(d.sortOrder ?? 0);
        setKotBotType(d.kotBotType ?? 'None');
        setServiceType(d.serviceType ?? 'All');
        setAvailableFrom(d.availableFrom ?? '');
        setAvailableTo(d.availableTo ?? '');
        setAvailableDays(d.availableDays ?? 0);
        setTaxRate(d.taxRate ?? 0);
        setTaxType(d.taxType ?? '');
        setManualCost(d.manualCost != null ? d.manualCost : '');
        if (Array.isArray(d.recipes)) {
          let k = 0;
          setRecipes(d.recipes.map((r: any) => ({
            key: k++, productId: r.productId ?? '', unitId: r.unitId ?? '',
            quantity: r.quantity ?? 1, unitCost: r.unitCost ?? 0, notes: r.notes ?? '',
          })));
          setRowKey(k);
        }
      }).catch(() => setError(t('restaurant.menuItem.failedToLoad')))
        .finally(() => setLoadingData(false));
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── tax selection: when user picks a tax from dropdown, auto-fill rate & type ── */
  const handleTaxSelect = useCallback((taxId: string) => {
    if (taxId === '') {
      setSelectedTaxId('');
      setTaxRate(0);
      setTaxType('');
      return;
    }
    const num = parseInt(taxId);
    setSelectedTaxId(num);
    const found = salesTaxes.find(t => t.taxId === num);
    if (found) {
      setTaxRate(found.taxPercentage);
      setTaxType(found.taxName);
    }
  }, [salesTaxes]);

  /* ── image upload helpers ── */
  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setImageStatus('error');
      setImageError(t('restaurant.menuItem.imageInvalidType'));
      e.target.value = '';
      return;
    }
    if (file.size > MAX_IMAGE_SOURCE_BYTES) {
      setImageStatus('error');
      setImageError(t('restaurant.menuItem.imageTooLarge'));
      e.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setLocalImagePreview(objectUrl);
    setImageStatus('uploading');
    setImageError(null);

    try {
      const compact = await compressImage(file);
      const res = await menuItemService.uploadImage(compact);
      const newUrl = res.data?.data?.imageUrl;
      if (newUrl) {
        setImageUrl(newUrl);
        setImageStatus('success');
      }
    } catch {
      setImageStatus('error');
      setImageError(t('restaurant.menuItem.imageUploadError'));
    } finally {
      URL.revokeObjectURL(objectUrl);
      setLocalImagePreview(null);
      e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImageUrl('');
    setImageStatus('idle');
    setImageError(null);
  };

  /* ── recipe helpers ── */
  const addRow = () => {
    setRecipes(p => [...p, { key: rowKey, productId: '', unitId: '', quantity: 1, unitCost: 0, notes: '' }]);
    setRowKey(k => k + 1);
  };
  const removeRow  = (key: number) => setRecipes(p => p.filter(r => r.key !== key));
  const updateRow  = (key: number, field: keyof RecipeRow, value: string | number) => {
    setRecipes(p => p.map(r => r.key !== key ? r : {
      ...r,
      [field]: (field === 'productId' || field === 'unitId')
        ? (value === '' ? '' : Number(value))
        : (field === 'quantity' || field === 'unitCost') ? Number(value) : value,
    }));
  };

  /* ── day-bit helpers ── */
  const isDayChecked = (i: number) => availableDays === 0 ? true : !!(availableDays & (1 << i));
  const toggleDay    = (i: number, checked: boolean) => {
    const bit = 1 << i;
    if (availableDays === 0) setAvailableDays(checked ? 0 : (127 & ~bit));
    else setAvailableDays(checked ? availableDays | bit : availableDays & ~bit);
  };

  /* ── validation ── */
  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim())                      errs.name           = t('common.required');
    if (menuCategoryId === '')             errs.menuCategoryId = t('common.required');
    if (price === '' || Number(price) < 0) errs.price          = t('restaurant.menuItem.priceRequired');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /* ── submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);

    const recipePayload: CreateMenuItemRecipeDTO[] = recipes
      .filter(r => r.productId !== '' && r.unitId !== '' && r.quantity > 0)
      .map(r => ({
        productId: r.productId as number,
        unitId:    r.unitId    as number,
        quantity:  r.quantity,
        unitCost:  r.unitCost,
        notes:     r.notes.trim() || undefined,
      }));

    const payload: CreateMenuItemDTO = {
      menuCode:             menuCode.trim() || undefined,
      name:                 name.trim(),
      description:          description.trim() || undefined,
      menuCategoryId:       menuCategoryId as number,
      price:                price as number,
      imageUrl:             imageUrl.trim() || undefined,
      isAvailable,
      isActive,
      prepTimeMinutes:      prepTimeMinutes !== '' ? prepTimeMinutes as number : undefined,
      sortOrder,
      kotBotType,
      serviceType,
      availableFrom:        availableFrom || undefined,
      availableTo:          availableTo   || undefined,
      availableDays,
      taxRate:              taxRate             === '' ? 0 : Number(taxRate),
      taxType:              taxType.trim()      || undefined,
      manualCost:           manualCost          !== '' ? Number(manualCost) : null,
      recipes:              recipePayload,
    };

    try {
      if (isEdit && id) await menuItemService.update(parseInt(id), payload);
      else await menuItemService.create(payload);
      navigate('/restaurant/menu-items');
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('restaurant.menuItem.failedToSave'));
      setSaving(false);
    }
  };

  /* ── derived ── */
  const recipeCost = recipes.filter(r => r.productId !== '').reduce((s, r) => s + r.quantity * r.unitCost, 0);
  const foodCost = manualCost !== '' ? Number(manualCost) : recipeCost;
  const foodCostPct = price !== '' && Number(price) > 0 ? (foodCost / Number(price)) * 100 : null;
  const hasCost = manualCost !== '' || recipes.some(r => r.productId !== '' && r.unitCost > 0);

  /* ── loading ── */
  if (loadingData) {
    return (
      <Layout>
        <div className="ims-create-body-page rest-create-page" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      </Layout>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Layout>
      <form onSubmit={handleSubmit} className="rest-create-page">
        <div className="ims-create-body-page">

          {/* ── Top bar ─────────────────────────────────────────────────── */}
          <div className="ims-top-bar">
            <div className="ims-top-bar-left">
              <button type="button" className="ims-back-btn" onClick={() => navigate('/restaurant/menu-items')}>
                <ArrowLeft size={13} />
                {t('restaurant.menuItem.title')}
              </button>
              <div>
                <h1 className="ims-page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ChefHat size={18} style={{ color: 'var(--ims-accent)' }} />
                  {isEdit ? t('restaurant.menuItem.editTitle') : t('restaurant.menuItem.createTitle')}
                </h1>
                <p className="ims-page-subtitle">
                  {isEdit
                    ? t('restaurant.menuItem.editSubtitle', 'Update item details, pricing and recipe')
                    : t('restaurant.menuItem.createSubtitle', 'Define a new menu item with pricing and recipe')}
                </p>
              </div>
            </div>
            <div className="ims-top-bar-right">
              <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/restaurant/menu-items')} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="ims-btn ims-btn-primary" disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</>
                  : (isEdit ? t('common.update') : t('common.save'))}
              </button>
            </div>
          </div>

          {/* ── Error banner ─────────────────────────────────────────────── */}
          {error && (
            <div className="ims-error-banner">
              <div className="ims-alert ims-alert-error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={15} />
                <span style={{ flex: 1 }}>{error}</span>
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            </div>
          )}

          {/* ── Body ─────────────────────────────────────────────────────── */}
          <div className="ims-create-body">

            {/* ════ LEFT — Basic Info ════════════════════════════════════ */}
            <div className="ims-panel-left" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* ── Basic Information card ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <ChefHat size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('restaurant.menuItem.basicInfo', 'Basic Information')}</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  {/* Name */}
                  <div className={`mi-field${fieldErrors.name ? ' mi-field-error' : ''}`}>
                    <label className="mi-label">
                      {t('restaurant.menuItem.name')}
                      <span className="mi-required">*</span>
                    </label>
                    <input
                      type="text"
                      className="mi-input"
                      value={name}
                      onChange={e => { setName(e.target.value); setFieldErrors(p => ({ ...p, name: '' })); }}
                      placeholder={t('restaurant.menuItem.namePlaceholder', 'e.g. Grilled Chicken')}
                      maxLength={150}
                      autoFocus
                    />
                    {fieldErrors.name && <span className="mi-err">{fieldErrors.name}</span>}
                  </div>

                  {/* Menu Code + Category row */}
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">
                        <Hash size={11} className="mi-label-icon" />
                        Menu Code
                      </label>
                      <input
                        type="text"
                        className="mi-input mi-input-mono"
                        value={menuCode}
                        onChange={e => setMenuCode(e.target.value)}
                        placeholder="e.g. ITEM-001"
                        maxLength={50}
                      />
                    </div>
                    <div className={`mi-field${fieldErrors.menuCategoryId ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">
                        {t('restaurant.menuItem.category')}
                        <span className="mi-required">*</span>
                      </label>
                      <select
                        className="mi-input"
                        value={menuCategoryId}
                        onChange={e => { setMenuCategoryId(e.target.value === '' ? '' : parseInt(e.target.value)); setFieldErrors(p => ({ ...p, menuCategoryId: '' })); }}
                      >
                        <option value="">{t('restaurant.menuItem.selectCategory')}</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}{c.typeName ? ` · ${c.typeName}` : ''}</option>
                        ))}
                      </select>
                      {fieldErrors.menuCategoryId && <span className="mi-err">{fieldErrors.menuCategoryId}</span>}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mi-field">
                    <label className="mi-label">{t('common.description')}</label>
                    <textarea
                      className="mi-input mi-textarea"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      rows={3}
                      placeholder={t('restaurant.menuItem.descriptionPlaceholder', 'Short description for the menu')}
                      maxLength={1000}
                    />
                  </div>

                  {/* Image upload */}
                  <div className="mi-field">
                    <label className="mi-label"><Image size={11} className="mi-label-icon" /> {t('restaurant.menuItem.image', 'Image')}</label>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{
                        width: 72, height: 72,
                        border: '2px dashed var(--border-color)',
                        borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', flexShrink: 0,
                        background: 'var(--bg-secondary)',
                      }}>
                        {(localImagePreview || imageUrl) ? (
                          <img
                            src={localImagePreview || imageUrl}
                            alt="Menu item preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Image size={26} strokeWidth={1.5} stroke="var(--text-secondary)" />
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            className="ims-btn ims-btn-secondary"
                            style={{ padding: '6px 12px', fontSize: 13 }}
                            onClick={() => imageFileInputRef.current?.click()}
                            disabled={imageStatus === 'uploading'}
                          >
                            {imageStatus === 'uploading' ? (
                              <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('restaurant.menuItem.imageUploading', 'Uploading…')}</>
                            ) : (
                              <><Upload size={13} /> {imageUrl ? t('restaurant.menuItem.imageChange', 'Change Image') : t('restaurant.menuItem.imageAdd', 'Add Image')}</>
                            )}
                          </button>
                          {imageUrl && imageStatus !== 'uploading' && (
                            <button type="button" className="ims-btn ims-btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={handleRemoveImage}>
                              {t('common.remove', 'Remove')}
                            </button>
                          )}
                        </div>
                        <span className="mi-hint">{t('restaurant.menuItem.imageHint', 'JPEG, PNG or WebP · compressed automatically')}</span>
                        {imageStatus === 'error' && imageError && <span className="mi-err">{imageError}</span>}
                      </div>
                    </div>
                    <input
                      ref={imageFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleImageFileChange}
                    />
                  </div>
                </div>
              </div>

              {/* ── Pricing card ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <BadgeDollarSign size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('restaurant.menuItem.pricingAndTiming', 'Pricing & Timing')}</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <div className="mi-row-2">
                    {/* Price */}
                    <div className={`mi-field${fieldErrors.price ? ' mi-field-error' : ''}`}>
                      <label className="mi-label">{t('restaurant.menuItem.price')} <span className="mi-required">*</span></label>
                      <input
                        type="number"
                        className="mi-input"
                        value={price}
                        onChange={e => { setPrice(e.target.value === '' ? '' : parseFloat(e.target.value)); setFieldErrors(p => ({ ...p, price: '' })); }}
                        min={0} step="0.01" placeholder="0.00"
                      />
                      {fieldErrors.price && <span className="mi-err">{fieldErrors.price}</span>}
                    </div>

                    {/* Prep time */}
                    <div className="mi-field">
                      <label className="mi-label"><Clock3 size={11} className="mi-label-icon" /> Prep Time</label>
                      <div className="mi-input-affix">
                        <input
                          type="number"
                          className="mi-input mi-input-has-suffix"
                          value={prepTimeMinutes}
                          onChange={e => setPrepTimeMinutes(e.target.value === '' ? '' : parseInt(e.target.value))}
                          min={0} placeholder="—"
                        />
                        <span className="mi-suffix">min</span>
                      </div>
                    </div>
                  </div>

                  {/* Sort order */}
                  <div className="mi-field" style={{ maxWidth: 110 }}>
                    <label className="mi-label">Sort Order</label>
                    <input type="number" className="mi-input" value={sortOrder}
                      onChange={e => setSortOrder(parseInt(e.target.value) || 0)} min={0} />
                  </div>

                  {/* Manual Cost */}
                  <div className="mi-field">
                    <label className="mi-label">
                      <BadgeDollarSign size={11} className="mi-label-icon" />
                      Menu Cost (Manual)
                    </label>
                    <input
                      type="number"
                      className="mi-input"
                      value={manualCost}
                      onChange={e => setManualCost(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      min={0} step="0.0001" placeholder="Leave blank to use recipe cost"
                    />
                    <span className="mi-hint">
                      {manualCost !== ''
                        ? 'Overrides recipe cost in reports and menu engineering'
                        : 'Optional — enter a fixed cost if not using a recipe'}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Status toggles ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Zap size={14} className="mi-header-icon" />
                  <span className="ims-card-title">{t('common.status')}</span>
                </div>
                <div className="ims-card-body mi-card-body" style={{ gap: 8 }}>
                  {([
                    { id: 'avail', label: 'Available on Menu', sub: 'Show this item to customers', val: isAvailable, set: setIsAvailable },
                    { id: 'activ', label: 'Active',            sub: 'Item is enabled in system',   val: isActive,    set: setIsActive },
                  ] as const).map(row => (
                    <label key={row.id} className={`mi-toggle-row${row.val ? ' mi-toggle-on' : ''}`}>
                      <div className="mi-toggle-text">
                        <span className="mi-toggle-label">{row.label}</span>
                        <span className="mi-toggle-sub">{row.sub}</span>
                      </div>
                      <div className={`mi-switch${row.val ? ' mi-switch-on' : ''}`} onClick={() => row.set(!row.val)}>
                        <div className="mi-switch-thumb" />
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* ════ CENTER — Recipe ══════════════════════════════════════ */}
            <div className="ims-panel-center">
              <div className="ims-card mi-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="ims-card-header mi-card-header" style={{ justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FlaskConical size={14} className="mi-header-icon" />
                    <span className="ims-card-title">{t('restaurant.recipe.title', 'Recipe / Ingredients')}</span>
                    {recipes.filter(r => r.productId !== '').length > 0 && (
                      <span className="mi-badge">{recipes.filter(r => r.productId !== '').length} items</span>
                    )}
                  </div>
                  <button type="button" className="mi-add-btn" onClick={addRow}>
                    <Plus size={13} />
                    Add Ingredient
                  </button>
                </div>

                <div style={{ flex: 1, overflowX: 'auto' }}>
                  {recipes.length === 0 ? (
                    <div className="mi-empty-state">
                      <FlaskConical size={40} className="mi-empty-icon" />
                      <p className="mi-empty-title">{t('restaurant.recipe.noIngredients', 'No ingredients yet')}</p>
                      <p className="mi-empty-sub">{t('restaurant.recipe.addIngredientHint', 'Click "Add Ingredient" to build the recipe')}</p>
                    </div>
                  ) : (
                    <table className="mi-recipe-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th style={{ width: 130 }}>Unit</th>
                          <th style={{ width: 90 }}>Qty</th>
                          <th style={{ width: 100 }}>Unit Cost</th>
                          <th>Notes</th>
                          <th style={{ width: 36 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipes.map(row => (
                          <tr key={row.key} className="mi-recipe-row">
                            <td>
                              <select className="mi-recipe-sel" value={row.productId}
                                onChange={e => updateRow(row.key, 'productId', e.target.value)}>
                                <option value="">Select product…</option>
                                {products.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select className="mi-recipe-sel" value={row.unitId}
                                onChange={e => updateRow(row.key, 'unitId', e.target.value)}>
                                <option value="">Unit…</option>
                                {units.map(u => (
                                  <option key={u.unitId} value={u.unitId}>{u.name}{u.shortName ? ` (${u.shortName})` : ''}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input type="number" className="mi-recipe-inp" value={row.quantity}
                                min={0.001} step="0.001"
                                onChange={e => updateRow(row.key, 'quantity', e.target.value)} />
                            </td>
                            <td>
                              <input type="number" className="mi-recipe-inp" value={row.unitCost}
                                min={0} step="0.0001"
                                onChange={e => updateRow(row.key, 'unitCost', e.target.value)} />
                            </td>
                            <td>
                              <input type="text" className="mi-recipe-inp" value={row.notes}
                                placeholder="Optional" maxLength={200}
                                onChange={e => updateRow(row.key, 'notes', e.target.value)} />
                            </td>
                            <td>
                              <button type="button" className="mi-del-btn" onClick={() => removeRow(row.key)}>
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Cost summary footer */}
                {hasCost && (
                  <div className="mi-cost-footer">
                    <div className="mi-cost-chip">
                      <span className="mi-cost-label">Food Cost</span>
                      <span className="mi-cost-value">${foodCost.toFixed(4)}</span>
                    </div>
                    {foodCostPct !== null && (
                      <div className={`mi-cost-chip${foodCostPct > 40 ? ' mi-cost-high' : foodCostPct > 25 ? ' mi-cost-mid' : ' mi-cost-ok'}`}>
                        <span className="mi-cost-label">Cost %</span>
                        <span className="mi-cost-value">{foodCostPct.toFixed(1)}%</span>
                      </div>
                    )}
                    {price !== '' && Number(price) > 0 && (
                      <div className="mi-cost-chip mi-cost-ok">
                        <span className="mi-cost-label">Margin</span>
                        <span className="mi-cost-value">${(Number(price) - foodCost).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ════ RIGHT — Settings ════════════════════════════════════ */}
            <div className="ims-panel-right" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* ── KOT / Service ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Settings2 size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Operational</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <div className="mi-field">
                    <label className="mi-label">KOT / BOT Printer</label>
                    <div className="mi-seg-group">
                      {(['None','Kitchen','Bar'] as const).map(v => (
                        <button key={v} type="button"
                          className={`mi-seg${kotBotType === v ? ' mi-seg-active' : ''}`}
                          onClick={() => setKotBotType(v)}>
                          {v === 'None' ? 'None' : v === 'Kitchen' ? 'KOT' : 'BOT'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mi-field">
                    <label className="mi-label">Service Type</label>
                    <div className="mi-seg-group">
                      {(['All','DineIn','Takeaway','RoomService'] as const).map(v => (
                        <button key={v} type="button"
                          className={`mi-seg${serviceType === v ? ' mi-seg-active' : ''}`}
                          onClick={() => setServiceType(v)}>
                          {v === 'All' ? 'All' : v === 'DineIn' ? 'Dine In' : v === 'Takeaway' ? 'Takeaway' : 'Room Svc'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Tax & Charges ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <Percent size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Tax &amp; Charges</span>
                </div>
                <div className="ims-card-body mi-card-body">

                  {/* Tax from reference */}
                  <div className="mi-field">
                    <label className="mi-label">
                      <Receipt size={11} className="mi-label-icon" />
                      Sales Tax
                    </label>
                    <select className="mi-input" value={selectedTaxId} onChange={e => handleTaxSelect(e.target.value)}>
                      <option value="">— No Tax —</option>
                      {salesTaxes.map(tx => (
                        <option key={tx.taxId} value={tx.taxId}>
                          {tx.taxName} ({tx.taxPercentage}%)
                        </option>
                      ))}
                    </select>
                    {salesTaxes.length === 0 && (
                      <span className="mi-hint">No sales taxes configured. Add them in Tax settings.</span>
                    )}
                  </div>

                  {/* Auto-filled from selection, editable override */}
                  {(taxRate !== 0 || taxType !== '') && (
                    <div className="mi-tax-preview">
                      <div className="mi-tax-chip">
                        <span>{taxType || 'Tax'}</span>
                        <strong>{taxRate}%</strong>
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* ── Availability ── */}
              <div className="ims-card mi-card">
                <div className="ims-card-header mi-card-header">
                  <CalendarCheck size={14} className="mi-header-icon" />
                  <span className="ims-card-title">Availability</span>
                </div>
                <div className="ims-card-body mi-card-body">
                  <div className="mi-row-2">
                    <div className="mi-field">
                      <label className="mi-label">From</label>
                      <input type="time" className="mi-input" value={availableFrom} onChange={e => setAvailableFrom(e.target.value)} />
                    </div>
                    <div className="mi-field">
                      <label className="mi-label">To</label>
                      <input type="time" className="mi-input" value={availableTo} onChange={e => setAvailableTo(e.target.value)} />
                    </div>
                  </div>

                  <div className="mi-field">
                    <label className="mi-label">Available Days</label>
                    <div className="mi-days-grid">
                      {DAYS.map((day, i) => (
                        <label key={day} className={`mi-day-pill${isDayChecked(i) ? ' mi-day-on' : ''}`}>
                          <input type="checkbox" style={{ display: 'none' }} checked={isDayChecked(i)} onChange={e => toggleDay(i, e.target.checked)} />
                          {day}
                        </label>
                      ))}
                      <label className={`mi-day-pill mi-day-all${availableDays === 0 ? ' mi-day-all-on' : ''}`}>
                        <input type="checkbox" style={{ display: 'none' }} checked={availableDays === 0}
                          onChange={e => setAvailableDays(e.target.checked ? 0 : 127)} />
                        All
                      </label>
                    </div>
                    <span className="mi-hint">All = available every day</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </form>
    </Layout>
  );
};

export default MenuItemCreatePage;
